import { Injectable, inject } from '@angular/core';
import { KYCStatusEnum } from '@common/enums/kyc-status.enum';
import { NafathStatusEnums } from '@common/enums/nafath.enum';
import { TranslateService } from '@ngx-translate/core';
import { ErrorHandlingService } from './error-handling.service';
import { DashboardService } from '../../pages/dashboard/dashboard.service';
import { AMLStatusEnum } from '@common/enums/aml.enum';
import { ProxyRequestStatus } from '@common/enums/proxy-request-status';
import { WathiqStatusEnums } from '@common/enums/wathiq.enum';
import { FirebaseRemoteConfigService } from './firebase-remote-config.service';

@Injectable({
  providedIn: 'root',
})
export class AuthorizedInvestorService {
  dashboardservice = inject(DashboardService);
  errorHandlingService = inject(ErrorHandlingService);
  translate = inject(TranslateService);
  // wathiq_rollback - Inject FirebaseRemoteConfigService for centralized wathiq flow status
  firebaseRemoteConfigService = inject(FirebaseRemoteConfigService);

  checkAuthorizedInvestorNavigation(): boolean {
    const investorProfile = this.dashboardservice.investorProfile.value;
    const investor = investorProfile?.investor;

    if (!investor) {
      return false;
    }

    // Helper function to display error messages
    const showError = (messageKey: string) => {
      this.errorHandlingService.showError({
        message: this.translate.instant(messageKey),
      });
    };

    // 1. **Handle DEPRECATED Statuses (KYC and Nafath)**
    if (
      investor.kycSubmissionRequestStatus === KYCStatusEnum.DEPRECATED &&
      investor.nafathRequestStatus === NafathStatusEnums.DEPRECATED
    ) {
      showError(
        'account_screen_authorized_investor_row_deprecated_kyc_and_nafath_error'
      );
      return false;
    }

    // 2. **Handle Individual DEPRECATED Statuses (KYC or Nafath)**
    if (investor.nafathRequestStatus === NafathStatusEnums.DEPRECATED) {
      showError(
        'account_screen_authorized_investor_row_deprecated_nafath_error'
      );
      return false;
    }

    if (investor.kycSubmissionRequestStatus === KYCStatusEnum.DEPRECATED) {
      showError('account_screen_authorized_investor_row_deprecated_kyc_error');
      return false;
    }

    // 3. **Check Nafath Statuses (Not Completed or Not Required)**
    if (
      investor.nafathRequestStatus !== NafathStatusEnums.COMPLETED &&
      investor.nafathRequestStatus !== NafathStatusEnums.NOT_REQUIRED
    ) {
      showError('common_nafath_status_not_completed_error');
      return false;
    }

    // wathiq_rollback - 4. **Check Wathiq Statuses (Not Completed) - for Corporate investors**
    // wathiq_rollback - Only check wathiq if flow is enabled (using centralized signal)
    if (
      this.firebaseRemoteConfigService.turn_on_watheeq_flow() &&
      investor.type === 2
    ) {
      const wathiqStatus = investor?.wathqStatus;

      // wathiq_rollback - Check wathiq status only if flow is enabled
      if (wathiqStatus !== WathiqStatusEnums.COMPLETED) {
        showError('common_wathiq_status_not_completed_error');
        return false;
      }
    }

    // 5. **Check Proxy Request Status**
    if (investor.proxyRequestStatus === ProxyRequestStatus.PENDING_SUBMISSION) {
      showError(
        'account_screen_authorized_investor_pending_submission_wakala_error'
      );
      return false;
    }

    if (investor.proxyRequestStatus === ProxyRequestStatus.PENDING_REVIEW) {
      showError(
        'account_screen_authorized_investor_pending_review_wakala_error'
      );
      return false;
    }

    // 6. **Handle KYC Submission Statuses (Pending, Rejected, Outdated, etc.)**
    const kycStatuses = [
      KYCStatusEnum.PENDING_SUBMISSION,
      KYCStatusEnum.REJECTED,
      KYCStatusEnum.OUTDATED,
    ];

    if (
      kycStatuses.includes(investor.kycSubmissionRequestStatus as KYCStatusEnum)
    ) {
      showError('account_screen_authorized_investor_not_completed_kyc_error');
      return false;
    }

    // Check for PENDING_REVIEW status separately
    if (investor.kycSubmissionRequestStatus === KYCStatusEnum.PENDING_REVIEW) {
      showError('account_screen_authorized_investor_pending_kyc_error');
      return false;
    }

    // 7. **Handle AML Statuses**
    if (investor.amlStatus === AMLStatusEnum.FAILED) {
      showError('common_aml_failed_warning_description');
      return false;
    }

    if (investor.amlStatus === AMLStatusEnum.PENDING_REVIEW) {
      showError('home_screen_kyc_pending_review_warning_title');
      return false;
    }

    return true;
  }
}
